import { useLayoutEffect, useState } from 'react';

const EDGE_PADDING = 16;
const ESTIMATED_POPOVER_HEIGHT = 335;
const GAP = 10;

interface UseDropdownDirectionParams {
  open: boolean;
  triggerRef: React.RefObject<HTMLElement | null>;
  listRef?: React.RefObject<HTMLElement | null>;
}

interface UseDropdownDirectionResult {
  openUp: boolean;
  anchorRect: DOMRect | null;
}

export default function useDropdownDirection({
  open,
  triggerRef,
  listRef,
}: UseDropdownDirectionParams): UseDropdownDirectionResult {
  const [openUp, setOpenUp] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const compute = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      setAnchorRect(rect);

      const spaceBelow = window.innerHeight - rect.bottom - EDGE_PADDING;
      const spaceAbove = rect.top - EDGE_PADDING;

      const popoverEl = listRef?.current;
      const measuredHeight =
        popoverEl && popoverEl.offsetHeight > 0
          ? popoverEl.offsetHeight
          : ESTIMATED_POPOVER_HEIGHT;

      const shouldOpenUp =
        spaceBelow < Math.min(measuredHeight, ESTIMATED_POPOVER_HEIGHT) + GAP &&
        spaceAbove > spaceBelow;

      setOpenUp(shouldOpenUp);
    };

    compute();
    const raf = window.requestAnimationFrame(compute);

    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, triggerRef, listRef]);

  return { openUp, anchorRect };
}
